import {
  Compilation,
  CompilationEntrySchema,
  CompilationPreview,
  CompilationSortOptionSchema,
  ContentCompilationSchema,
} from "@turtlewave/types";
import { z } from "zod";

import { ContentValidationError } from "../content/errors";

// The primitive shapes (compilation reference, entry, publish date, sort
// option) are validated identically by the API and live in
// @turtlewave/types. Re-exported so existing importers keep working.
export { CompilationEntrySchema };
export const CompilationMinimalSchema = ContentCompilationSchema;

/**
 * Zod schema for CompilationPreview (without compilation_entries)
 */
export const CompilationPreviewSchema = z.object({
  compilation_id: z.string().min(1),
  title: z.string().min(1),
  type: z.string(),
  default_sort: CompilationSortOptionSchema,
  description: z.string(),
  preview_url: z.string().url().nullable(),
  content_preview_urls: z.array(z.string().url()),
  last_edited_timestamp: z.string().datetime(),
  entry_count: z.number().int().min(0),
});

/**
 * Zod schema for Compilation (full with entries)
 */
export const CompilationSchema = CompilationPreviewSchema.extend({
  compilation_entries: z.array(CompilationEntrySchema),
});

/**
 * Validates a compilation response
 * @param data - Unknown data to validate
 * @returns Validated Compilation
 * @throws ContentValidationError if validation fails
 */
export function validateCompilation(data: unknown): Compilation {
  try {
    return CompilationSchema.parse(data);
  } catch (error) {
    throw new ContentValidationError(
      "Compilation data validation failed",
      error,
    );
  }
}

/**
 * Validates a compilation preview response
 * @param data - Unknown data to validate
 * @returns Validated CompilationPreview
 * @throws ContentValidationError if validation fails
 */
export function validateCompilationPreview(data: unknown): CompilationPreview {
  try {
    return CompilationPreviewSchema.parse(data);
  } catch (error) {
    throw new ContentValidationError(
      "Compilation preview data validation failed",
      error,
    );
  }
}

/**
 * Zod schema for an array of CompilationPreview
 */
export const CompilationPreviewArraySchema = z.array(CompilationPreviewSchema);

/**
 * Validates an array of compilation previews
 * @param data - Unknown data to validate
 * @returns Validated array of CompilationPreview
 * @throws ContentValidationError if validation fails
 */
export function validateCompilationPreviewArray(
  data: unknown,
): CompilationPreview[] {
  try {
    return CompilationPreviewArraySchema.parse(data);
  } catch (error) {
    throw new ContentValidationError(
      "Compilation preview array validation failed",
      error,
    );
  }
}
